import { ArrowLeft, Clock, Navigation, Phone, PlugZap, Share2 } from 'lucide-react'
import { Link, Navigate, useNavigate, useParams } from 'react-router'
import { toast } from 'sonner'
import { uploadUrl } from '@/api/client'
import { useStations } from '@/api/driver'
import type { MapCharger, MapStation } from '@/api/types'
import { EmptyState, ErrorState, LoadingBlock, StatusPill } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { connectorRate, connectorState } from '@/lib/charging'
import { kw, npr } from '@/lib/format'
import { directionsUrl, openNow } from '@/lib/geo'
import { useI18n } from '@/lib/i18n'
import { freeConnectors } from './MapPage'
import { FavouriteButton, ReportProblemButton, StationReviews, WatchCard } from './StationExtras'

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const
const AMENITY: Record<string, string> = { restroom: 'Restroom', wifi: 'Wi-Fi', cafe: 'Restaurant / cafe', restaurant: 'Restaurant / cafe', parking: 'Parking', shop: 'Shop', lounge: 'Lounge', hotel: 'Hotel', snacks: 'Snacks', cctv: 'CCTV security' }
const titleCase = (s: string) => s.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

export function StationPage() {
  const { id } = useParams()
  const { t } = useI18n()
  const navigate = useNavigate()
  const q = useStations({ station_id: Number(id) })
  if (q.isLoading) return <LoadingBlock rows={5} />
  if (q.error) return <ErrorState error={q.error} onRetry={() => q.refetch()} />
  const s = q.data?.stations.find((x) => x.id === Number(id))
  if (!s) return <EmptyState title={t('Station not found')} action={<Link to="/" className="underline">{t('Back to the map')}</Link>} />
  return <StationView station={s} onBack={() => (history.length > 1 ? navigate(-1) : navigate('/'))} />
}

/**
 * Where a QR sticker points. `/c/:chargerId/:connector` (one sticker per
 * connector, as the dashboard prints them) opens Start on that connector;
 * `/c/:chargerId` opens Start on a one-connector charger and the station to
 * pick from otherwise. Charger ids match ignoring case; an unknown connector
 * falls back to the station.
 */
export function ChargerLink() {
  const { chargerId = '', connector } = useParams()
  const { t } = useI18n()
  const q = useStations()
  if (q.isLoading) return <LoadingBlock rows={4} />
  if (q.error) return <ErrorState error={q.error} onRetry={() => q.refetch()} />
  const s = q.data?.stations.find((x) => x.chargers.some((c) => c.id.toLowerCase() === chargerId.toLowerCase()))
  if (!s) return <EmptyState icon={<PlugZap />} title={t('Charger {id} not found', { id: chargerId })} body={t('Check the ID on the charger, or find it on the map.')} action={<Link to="/" className="underline">{t('Open the map')}</Link>} />
  const c = s.chargers.find((x) => x.id.toLowerCase() === chargerId.toLowerCase())!
  const k = connector ? c.connectors.find((x) => String(x.connector_number) === connector) : c.connectors.length === 1 ? c.connectors[0] : undefined
  if (k) return <Navigate to={`/charge/${encodeURIComponent(c.id)}/${k.connector_number}`} replace />
  return <Navigate to={`/stations/${s.id}#${encodeURIComponent(c.id)}`} replace />
}

function StationView({ station: s, onBack }: { station: MapStation; onBack: () => void }) {
  const { t } = useI18n()
  const open = openNow(s.opening_hours)
  const photos = [...s.station_photos].sort((a, b) => a.display_order - b.display_order)
  const allDay = s.opening_hours.length === 7 && s.opening_hours.every((h) => !h.closed && h.open === '00:00' && (h.close === '23:59' || h.close === '24:00' || h.close === '00:00'))
  const share = async () => {
    const url = location.href
    try {
      if (navigator.share) await navigator.share({ title: s.name, url })
      else {
        await navigator.clipboard.writeText(url)
        toast.success(t('Link copied'))
      }
    } catch {
      /* cancelled */
    }
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <button onClick={onBack} className="-ml-2 flex items-center gap-1 rounded-lg px-2 py-1 text-sm text-text-2 hover:bg-surface-3">
          <ArrowLeft className="size-4" /> {t('Back')}
        </button>
        <div className="flex items-center gap-1">
          <FavouriteButton stationId={s.id} />
          <Button variant="ghost" size="sm" onClick={share}><Share2 /> {t('Share')}</Button>
        </div>
      </div>

      {photos.length > 0 && (
        <div className="no-scrollbar -mx-4 mb-4 flex snap-x gap-2 overflow-x-auto px-4">
          {photos.map((p) => (
            <img key={p.id} src={uploadUrl(p.photo_path)} alt="" loading="lazy" className="aspect-[4/3] w-72 shrink-0 snap-start rounded-xl bg-surface-3 object-cover sm:w-80" />
          ))}
        </div>
      )}

      <h1 className="text-2xl font-semibold tracking-tight">{s.name}</h1>
      <p className="mt-1 text-sm text-text-2">{[s.cpo_name, s.address].filter(Boolean).join(' · ')}</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {freeConnectors(s) ? <StatusPill tone="success">{t('{n} free', { n: freeConnectors(s) })}</StatusPill> : <StatusPill tone="neutral">{t('None free')}</StatusPill>}
        {open === true && <StatusPill tone="success">{allDay ? t('Open 24 hours') : t('Open now')}</StatusPill>}
        {open === false && <StatusPill tone="danger">{t('Closed now')}</StatusPill>}
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 sm:flex">
        {s.latitude != null && s.longitude != null && (
          <a href={directionsUrl(s.latitude, s.longitude)} target="_blank" rel="noreferrer" className="flex h-11 items-center justify-center gap-2 rounded-xl bg-primary px-4 text-sm font-medium text-primary-fg sm:h-10">
            <Navigation className="size-4" /> {t('Directions')}
          </a>
        )}
        {s.contacts[0] && (
          <a href={`tel:${s.contacts[0].phone}`} className="flex h-11 items-center justify-center gap-2 rounded-xl border border-border bg-surface px-4 text-sm font-medium sm:h-10">
            <Phone className="size-4" /> {t('Call station')}
          </a>
        )}
      </div>

      {s.description && <p className="mt-4 text-sm text-text-2">{s.description}</p>}

      <h2 className="mb-2 mt-6 text-sm font-semibold uppercase tracking-wide text-text-2">{t('Chargers')}</h2>
      <div className="space-y-3">
        {s.chargers.map((c) => <ChargerCard key={c.id} charger={c} station={s} />)}
      </div>
      {freeConnectors(s) <= 1 && <div className="mt-3"><WatchCard station={s} /></div>}
      <StationReviews station={s} />

      {(s.amenities.length > 0 || s.opening_hours.length > 0) && (
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          {s.opening_hours.length > 0 && (
            <Card className="p-4">
              <p className="mb-2 flex items-center gap-2 text-sm font-semibold"><Clock className="size-4" /> {t('Opening hours')}</p>
              {allDay ? (
                <p className="text-sm text-text-2">{t('Open 24 hours, every day')}</p>
              ) : (
                <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                  {DAYS.map((d) => {
                    const h = s.opening_hours.find((x) => x.day === d)
                    return [<dt key={d} className="text-text-2">{t(titleCase(d))}</dt>, <dd key={`${d}v`} className="tabular">{!h || h.closed ? t('Closed') : `${h.open} – ${h.close}`}</dd>]
                  })}
                </dl>
              )}
            </Card>
          )}
          {s.amenities.length > 0 && (
            <Card className="p-4">
              <p className="mb-2 text-sm font-semibold">{t('While you charge')}</p>
              <div className="flex flex-wrap gap-1.5">
                {s.amenities.map((a) => <span key={a} className="rounded-full bg-surface-3 px-2.5 py-1 text-xs">{t(AMENITY[a] ?? titleCase(a))}</span>)}
              </div>
            </Card>
          )}
        </div>
      )}
      {s.contacts.length > 1 && (
        <div className="mt-3 text-sm text-text-2">
          {s.contacts.slice(1).map((c) => <p key={c.phone}>{c.name}{c.role ? ` (${c.role})` : ''}: <a className="underline" href={`tel:${c.phone}`}>{c.phone}</a></p>)}
        </div>
      )}
    </div>
  )
}

function ChargerCard({ charger: c, station }: { charger: MapCharger; station: MapStation }) {
  const { t } = useI18n()
  const navigate = useNavigate()
  return (
    <Card id={c.id} className="overflow-hidden">
      <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-2.5">
        <p className="text-sm font-semibold">{c.charger_type ?? ''} {kw(c.max_power_kw)} <span className="font-normal text-text-2">· {c.id}</span></p>
        {c.status === 'Offline' && <StatusPill tone="neutral">{t('Offline')}</StatusPill>}
      </div>
      <div className="divide-y divide-border">
        {c.connectors.map((k) => {
          const st = connectorState(c, k)
          const rate = connectorRate(k)
          return (
            <div key={k.connector_number} className="flex items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="font-medium">{k.connector_type} <span className="text-sm font-normal text-text-2">· {kw(k.max_power_kw ?? c.max_power_kw)}</span></p>
                <p className="mt-0.5 flex flex-wrap items-center gap-2 text-[13px] text-text-2">
                  <StatusPill tone={st.tone}>{t(st.label)}</StatusPill>
                  {rate != null ? `${npr(rate)} / kWh` : k.pricing_plan ? t('Time-of-day rate') : null}
                </p>
              </div>
              <Button size="md" variant={st.startable ? 'brand' : 'outline'} disabled={!st.startable} onClick={() => navigate(`/charge/${encodeURIComponent(c.id)}/${k.connector_number}`)}>
                {t('Start')}
              </Button>
            </div>
          )
        })}
      </div>
      <div className="border-t border-border px-4 py-2"><ReportProblemButton station={station} charger={c} /></div>
    </Card>
  )
}
